import { ENABLED_ACCESS_TOKEN_STORE } from '@shared/constants/Keys';
import { getBaseApiUrl } from '@shared/constants/Urls';
import IResult from '@shared/models/result/IResult';
import InvalidResult from '@shared/models/result/InvalidResult';
import NoResponseResult from '@shared/models/result/NoResponseResult';
import NotFoundResult from '@shared/models/result/NotFoundResult';
import OfflineResult from '@shared/models/result/OfflineResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnauthorizedResult from '@shared/models/result/UnauthorizedResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import axios, { AxiosResponse } from 'axios';

// generic http method for interacting with the enabled api
export const sendPost = async <T>(url: string, body: any, baseUrl?: string): Promise<IResult<T>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.post<T | string[]>(fullUrl, body);
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const sendPostForFile = async (url: string, body: any, baseUrl?: string): Promise<IResult<Blob>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.post<Blob | string[]>(fullUrl, body, {
            responseType: 'blob',
        });
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const uploadFile = async <T>(url: string, file: File, baseUrl?: string): Promise<IResult<T>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        let formData = new FormData();
        formData.append('file', file);
        const response = await axios.post<T | string[]>(fullUrl, formData);
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const putFile = async <T>(
    url: string,
    file: File,
    progressCallback?: (number: number) => void
): Promise<IResult<T>> => {
    try {
        let instance = axios.create();
        instance.defaults.headers.common = {};
        const response = await instance.put(url, file, {
            headers: {
                'Content-Type': getFileType(file.type),
            },
            onUploadProgress: (progressEvent) => {
                if (progressCallback && progressEvent.total) {
                    progressCallback(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
                }
            },
        });
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

const getFileType = (fileType: string) => {
    // override file type for content type since certain browsers just make up their own rules
    if (fileType == 'audio/mpeg') return 'audio/mp3';
    return fileType;
};

export const sendGet = async <T>(url: string, baseUrl?: string): Promise<IResult<T>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.get<T | string[]>(fullUrl);
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const sendGetFile = async (url: string, baseUrl?: string): Promise<IResult<Blob>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.get<Blob | string[]>(fullUrl, {
            responseType: 'blob',
        });
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const sendPut = async <T>(url: string, body: any, baseUrl?: string): Promise<IResult<T>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.put<T | string[]>(fullUrl, body);
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const sendDelete = async <T>(url: string, baseUrl?: string): Promise<IResult<T>> => {
    let fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        setToken();
        const response = await axios.delete(fullUrl);
        return handleResponse(response);
    } catch (error) {
        return handleResponse(error.response);
    }
};

export const queryStringSerialize = (model: any): string => {
    let stringArray = [];
    for (let property in model) {
        if (Object.prototype.hasOwnProperty.call(model, property) && model[property] != null) {
            stringArray.push(encodeURIComponent(property) + '=' + encodeURIComponent(model[property]));
        }
    }

    return stringArray.join('&');
};

const setToken = () => {
    // pull token from local store
    const accessToken = sessionStorage.getItem(ENABLED_ACCESS_TOKEN_STORE);
    axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
    // set to default header
    if (accessToken !== undefined) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
};

export const overrideToken = (token: string) => {
    axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const setCommandToken = (token: string) => {
    axios.defaults.headers.common['X-COMMAND-TOKEN'] = token;
};

// maps an axios response to a result
const handleResponse = <T>(response: AxiosResponse<T | string[]>): IResult<T> => {
    if (!navigator.onLine) {
        return new OfflineResult();
    }
    if (!response) return new NoResponseResult();
    if (response.status === 200 || response.status === 204) {
        if (response.data === undefined) {
            return new SuccessResult({} as T);
        }
        return new SuccessResult(response.data as T);
    } else if (response.status === 400) {
        let errors = response.data as string[];
        let error = errors[0];
        if (error == undefined) {
            let attributeErrorNames = Object.getOwnPropertyNames(errors);
            if (attributeErrorNames[0] != undefined && errors[attributeErrorNames[0] as any] != null) {
                error = errors[attributeErrorNames[0] as any];
            } else {
                error = (response.data as any)['Responses[0].Content'][0];
            }
        }
        return new InvalidResult(error);
    } else if (response.status === 404) {
        return new NotFoundResult();
    } else if (response.status === 403) {
        return new UnauthorizedResult();
    }

    return new UnexpectedResult();
};
